export const QTYPES = {
  MCQ: 'mcq',
  GRID: 'grid',
  CODING: 'coding',
  FILE: 'file',
};

export const demoQuestions = [
  {
    id: 1,
    type: QTYPES.MCQ,
    category: 'JavaScript',
    difficulty: 'Medium',
    points: 10,
    title: 'Closures & Lexical Scope',
    body: `What will the following code output to the console?

\`\`\`javascript
function makeCounter() {
  let count = 0;
  return function () {
    count++;
    return count;
  };
}

const c1 = makeCounter();
console.log(c1()); // line A
console.log(c1()); // line B

const c2 = makeCounter();
console.log(c2()); // line C
\`\`\``,
    options: [
      { id: 'a', text: 'Line A: 1,  Line B: 2,  Line C: 1' },
      { id: 'b', text: 'Line A: 1,  Line B: 1,  Line C: 1' },
      { id: 'c', text: 'Line A: 0,  Line B: 1,  Line C: 0' },
      { id: 'd', text: 'Line A: 1,  Line B: 2,  Line C: 3' },
    ],
    correct: 'a',
    explanation:
      'Each call to makeCounter() creates a new closure with its own lexically scoped count variable. c1 and c2 maintain completely independent state.',
  },
  {
    id: 2,
    type: QTYPES.MCQ,
    category: 'Async JavaScript',
    difficulty: 'Hard',
    points: 10,
    title: 'Event Loop & Execution Order',
    body: `What is the correct output order for the following code?

\`\`\`javascript
console.log('A');

setTimeout(() => console.log('B'), 0);

Promise.resolve()
  .then(() => console.log('C'))
  .then(() => console.log('D'));

console.log('E');
\`\`\``,
    options: [
      { id: 'a', text: 'A → B → C → D → E' },
      { id: 'b', text: 'A → E → C → D → B' },
      { id: 'c', text: 'A → E → B → C → D' },
      { id: 'd', text: 'A → C → E → D → B' },
    ],
    correct: 'b',
    explanation:
      'Synchronous code runs first (A, E). Microtasks (Promise callbacks) drain before macrotasks (setTimeout). So microtask queue: C → D, then macrotask queue: B.',
  },
  {
    id: 3,
    type: QTYPES.MCQ,
    category: 'ES6+',
    difficulty: 'Easy',
    points: 10,
    title: 'Array Destructuring with Rest',
    body: `What does the following destructuring assignment produce?

\`\`\`javascript
const [a, , b, ...rest] = [1, 2, 3, 4, 5, 6];
console.log(a, b, rest);
\`\`\``,
    options: [
      { id: 'a', text: 'a=1, b=3, rest=[4, 5, 6]' },
      { id: 'b', text: 'a=1, b=2, rest=[3, 4, 5, 6]' },
      { id: 'c', text: 'a=1, b=3, rest=[5, 6]' },
      { id: 'd', text: 'a=1, b=2, rest=[4, 5, 6]' },
    ],
    correct: 'a',
    explanation:
      'The pattern [a, , b, ...rest] skips index 1 (value 2) with an empty slot. a gets index 0 (1), b gets index 2 (3), and rest collects all remaining elements [4, 5, 6].',
  },
  {
    id: 4,
    type: QTYPES.MCQ,
    category: 'JavaScript',
    difficulty: 'Medium',
    points: 10,
    title: 'Array.prototype.reduce',
    body: `What is the output of the following code?

\`\`\`javascript
const nums = [1, 2, 3, 4, 5];

const result = nums.reduce((acc, curr) => {
  if (curr % 2 !== 0) return acc;
  return [...acc, curr * 2];
}, []);

console.log(result);
\`\`\``,
    options: [
      { id: 'a', text: '[4, 8]' },
      { id: 'b', text: '[2, 4, 6]' },
      { id: 'c', text: '[2, 6, 10]' },
      { id: 'd', text: '[4, 8, 10]' },
    ],
    correct: 'a',
    explanation:
      'The reduce filters to even numbers only (2 and 4), then doubles them. 2 × 2 = 4, 4 × 2 = 8. Odd numbers (1, 3, 5) are skipped by returning acc unchanged.',
  },
  {
    id: 5,
    type: QTYPES.MCQ,
    category: 'Algorithms',
    difficulty: 'Medium',
    points: 10,
    title: 'Time Complexity Analysis',
    body: `What is the time complexity of the following function?

\`\`\`javascript
function findPair(arr, target) {
  const seen = new Set();

  for (const num of arr) {
    const complement = target - num;
    if (seen.has(complement)) {
      return [complement, num];
    }
    seen.add(num);
  }

  return null;
}
\`\`\``,
    options: [
      { id: 'a', text: 'O(n²) — nested iteration over the array' },
      { id: 'b', text: 'O(n log n) — requires sorting step' },
      { id: 'c', text: 'O(n) — single pass with O(1) Set lookups' },
      { id: 'd', text: 'O(log n) — binary search approach' },
    ],
    correct: 'c',
    explanation:
      'The function uses a hash set for O(1) average-case lookups. The single for-loop is O(n), and each Set.has() and Set.add() is O(1) amortized, yielding overall O(n) time complexity.',
  },
  {
    id: 6,
    type: QTYPES.MCQ,
    category: 'DOM & Events',
    difficulty: 'Medium',
    points: 10,
    title: 'Event Bubbling Behaviour',
    body: `What is logged when a user clicks the inner \`<button>\`?

\`\`\`javascript
document.getElementById('outer').addEventListener('click', () => {
  console.log('outer');
});

document.getElementById('inner').addEventListener('click', () => {
  console.log('inner');
});
\`\`\`
\`\`\`html
<div id="outer">
  <button id="inner">Click Me</button>
</div>
\`\`\``,
    options: [
      { id: 'a', text: '"inner" only — click does not propagate' },
      { id: 'b', text: '"outer" first, then "inner" — capture phase default' },
      { id: 'c', text: '"inner" first, then "outer" — bubbling phase default' },
      { id: 'd', text: '"outer" only — inner listener is shadowed' },
    ],
    correct: 'c',
    explanation:
      'DOM events bubble upward by default. The click fires on the target (inner button) first, then propagates up to ancestor elements. Capture phase is opt-in via the third addEventListener argument.',
  },
  {
    id: 7,
    type: QTYPES.CODING,
    category: 'JavaScript',
    difficulty: 'Medium',
    points: 10,
    language: 'javascript',
    title: 'Implement groupBy',
    body: `Implement a \`groupBy\` function that groups an array of objects by a specified key.

**Requirements:**
- Return a new object where each key is a unique value of the specified property
- Each key maps to an array of objects that share that value
- Maintain insertion order within each group
- Handle empty arrays gracefully

**Constraints:** O(n) time complexity expected`,
    starterCode: `/**
 * Groups an array of objects by a specified key.
 *
 * @param {Object[]} array - Input array of objects
 * @param {string}   key   - The key to group by
 * @returns {Object}         Grouped object
 */
function groupBy(array, key) {
  // TODO: Implement using Array.prototype.reduce
  // Hint: acc[groupKey] should be an array

}

// ─── Test Cases (do not modify) ─────────────────────────────────
const engineers = [
  { id: 1, department: 'Engineering', level: 'Senior' },
  { id: 2, department: 'Design',      level: 'Mid'    },
  { id: 3, department: 'Engineering', level: 'Junior' },
  { id: 4, department: 'Product',     level: 'Senior' },
  { id: 5, department: 'Design',      level: 'Senior' },
];

console.log(JSON.stringify(groupBy(engineers, 'department'), null, 2));
// Expected:
// {
//   "Engineering": [{ id: 1, ... }, { id: 3, ... }],
//   "Design":      [{ id: 2, ... }, { id: 5, ... }],
//   "Product":     [{ id: 4, ... }]
// }

console.log(groupBy([], 'department')); // Expected: {}
`,
  },
  {
    id: 8,
    type: QTYPES.CODING,
    category: 'Python',
    difficulty: 'Hard',
    points: 10,
    language: 'python',
    title: 'Longest Substring Without Repeating Characters',
    body: `Implement a function to find the **length** of the longest substring without repeating characters.

**Requirements:**
- Solve using the sliding window technique
- Target time complexity: O(n)
- Target space complexity: O(min(m, n)) where m is the character set size

**Examples:**
- \`"abcabcbb"\` → \`3\` (window: \`"abc"\`)
- \`"bbbbb"\` → \`1\` (window: \`"b"\`)
- \`"pwwkew"\` → \`3\` (window: \`"wke"\`)`,
    starterCode: `def length_of_longest_substring(s: str) -> int:
    """
    Find the length of the longest substring without repeating characters.

    Args:
        s: Input string

    Returns:
        Integer length of the longest valid substring
    """
    # TODO: Implement using the sliding window technique
    # Hint: Use a dict/set to track character positions
    pass


# ─── Test Runner (do not modify) ────────────────────────────────
if __name__ == "__main__":
    test_cases = [
        ("abcabcbb", 3),
        ("bbbbb",    1),
        ("pwwkew",   3),
        ("",         0),
        ("au",       2),
        ("dvdf",     3),
    ]
    passed = 0
    for s, expected in test_cases:
        result = length_of_longest_substring(s)
        ok     = result == expected
        status = "PASS" if ok else "FAIL"
        if ok: passed += 1
        print(f"[{status}]  '{s}'  →  expected {expected}, got {result}")
    print(f"\\n{passed}/{len(test_cases)} tests passed")
`,
  },
  {
    id: 9,
    type: QTYPES.GRID,
    category: 'Algorithms',
    difficulty: 'Medium',
    points: 10,
    title: 'Data Structure Complexity Table',
    body: 'Complete the time complexity table. Fill in the missing Big O values for each data structure operation. Use standard notation: O(1), O(n), O(log n), O(n log n).',
    rows: ['Array', 'Linked List', 'Hash Table', 'BST (Balanced)'],
    cols: ['Access', 'Search', 'Insert', 'Delete'],
    cells: {
      '0_0': 'O(1)',     // Array Access     ← pre-filled
      '0_1': null,       // Array Search      ← blank
      '0_2': null,       // Array Insert      ← blank
      '0_3': 'O(n)',     // Array Delete      ← pre-filled
      '1_0': null,       // LL Access         ← blank
      '1_1': 'O(n)',     // LL Search         ← pre-filled
      '1_2': 'O(1)*',   // LL Insert         ← pre-filled
      '1_3': null,       // LL Delete         ← blank
      '2_0': null,       // HT Access         ← blank
      '2_1': null,       // HT Search         ← blank
      '2_2': 'O(1)',     // HT Insert         ← pre-filled
      '2_3': 'O(1)',     // HT Delete         ← pre-filled
      '3_0': 'O(log n)', // BST Access        ← pre-filled
      '3_1': null,       // BST Search        ← blank
      '3_2': null,       // BST Insert        ← blank
      '3_3': 'O(log n)', // BST Delete        ← pre-filled
    },
    answers: {
      '0_1': 'O(n)',
      '0_2': 'O(n)',
      '1_0': 'O(n)',
      '1_3': 'O(1)',
      '2_0': 'O(1)',
      '2_1': 'O(1)',
      '3_1': 'O(log n)',
      '3_2': 'O(log n)',
    },
  },
  {
    id: 10,
    type: QTYPES.FILE,
    category: 'Portfolio',
    difficulty: 'N/A',
    points: 10,
    title: 'Portfolio Submission',
    body: 'Upload your technical portfolio, system design document, or coding resume. This document will be evaluated by our technical team as part of the assessment.',
    acceptedFormats: ['.pdf', '.zip'],
    maxSizeMB: 10,
  },
];

export function computeResults(answers) {
  let totalEarned = 0;
  const maxScore = demoQuestions.reduce((s, q) => s + q.points, 0);
  const questionResults = {};

  demoQuestions.forEach((q) => {
    let earned = 0;

    if (q.type === QTYPES.MCQ) {
      const correct = answers[q.id] === q.correct;
      earned = correct ? q.points : 0;
      questionResults[q.id] = { earned, max: q.points, correct };
    } else if (q.type === QTYPES.CODING) {
      const code = answers[q.id]?.code || '';
      const hasContent = code.replace(q.starterCode || '', '').trim().length > 20;
      earned = hasContent ? Math.round(q.points * 0.8) : Math.round(q.points * 0.3);
      questionResults[q.id] = { earned, max: q.points, correct: false, partial: true };
    } else if (q.type === QTYPES.GRID) {
      const gridAnswers = answers[q.id] || {};
      const blanks = Object.entries(q.cells).filter(([, v]) => v === null);
      const correct = blanks.filter(([k]) => {
        const userVal = (gridAnswers[k] || '').trim().replace(/\s+/g, ' ').toLowerCase();
        const correctVal = q.answers[k].toLowerCase();
        return userVal === correctVal;
      });
      earned = Math.round((correct.length / blanks.length) * q.points);
      questionResults[q.id] = { earned, max: q.points, correct: false, partial: true };
    } else if (q.type === QTYPES.FILE) {
      earned = answers[q.id]?.fileName ? q.points : 0;
      questionResults[q.id] = { earned, max: q.points, correct: !!answers[q.id]?.fileName };
    }

    totalEarned += earned;
  });

  return {
    score: totalEarned,
    maxScore,
    percentage: Math.round((totalEarned / maxScore) * 100),
    questionResults,
  };
}
